import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import { DoubleArrow, Edit, Delete } from "@mui/icons-material";
import { EditShiftPanel } from "./EditShiftPanel";
import { themes } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";

export const ViewShiftPanel = ({
  onClose,
  editShiftDetails,
  reCallApi,
  setUpdateData,
  setGetId,
  screenAccess,
}) => {
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const { domain } = useSelector((state) => state.tokenReducer);

  const handleEditShift = () => {
    setIsEditPanelOpen(true);
  };

  const handleEditClosePanel = () => {
    setIsEditPanelOpen(false);
  };

  const handleDeleteSft = async () => {
    const response = await customAPI(
      `${domain}administration/shiftroster/shift/shifts/${editShiftDetails?.id}/`,
      "PUT",
      {
        name: editShiftDetails?.name.trim(),
        start_time: editShiftDetails?.start_time,
        end_time: editShiftDetails?.end_time,
        min_start_time: editShiftDetails?.min_start_time,
        max_start_time: editShiftDetails?.max_start_time,
        min_end_time: editShiftDetails?.min_end_time,
        max_end_time: editShiftDetails?.max_end_time,
        ot_start_time: editShiftDetails?.ot_start_time,
        ot_end_time: editShiftDetails?.ot_end_time,
        ot_max_start_time: editShiftDetails?.ot_max_start_time,
        ot_min_end_time: editShiftDetails?.ot_min_end_time,
        duration: editShiftDetails?.duration,
        start_early: editShiftDetails?.start_early,
        start_delay: editShiftDetails?.start_delay,
        end_early: editShiftDetails?.end_early,
        end_delay: editShiftDetails?.end_delay,
        ot_start_delay: editShiftDetails?.ot_start_delay,
        ot_end_early: editShiftDetails?.ot_end_early,
        color_code: editShiftDetails?.color_code,
        is_break_allowed: editShiftDetails?.is_break_allowed,
        break_duration: editShiftDetails?.break_duration,
        is_break_part_of_shift: editShiftDetails?.is_break_part_of_shift,
        shift_duration_hours: editShiftDetails?.shift_duration_hours,
        shift_working_hours: editShiftDetails?.shift_working_hours,
        isdeleted: true,
      }
    );
    if (response.ok) {
      showToast("success", "Shift details deleted successfully");
      reCallApi(true);
      setUpdateData(true);
      onClose();
    }
  };

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper elevation={0} sx={{ borderRadius: 0 }}>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <IconButton
                onClick={onClose}
                sx={{
                  color: themes?.grayColor,
                  "&:hover": { backgroundColor: themes?.primary },
                }}
              >
                <DoubleArrow />
              </IconButton>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {editShiftDetails?.is_editable && (
                  <Tooltip title="Delete" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={handleDeleteSft}
                      sx={{
                        color: themes?.redColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                        mt: 0.5,
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                  <IconButton
                    onClick={() => {
                      if (screenAccess) {
                        handleEditShift();
                      } else {
                        showToast(
                          "error",
                          `Access denied: You are not authorized to edit shift`
                        );
                      }
                    }}
                    sx={{
                      color: themes?.greenColor,
                      backgroundColor: themes?.primary,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: themes?.primary },
                      mr: 2,
                      mt: 0.5,
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ ml: 2.2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: themes?.headLine,
                  textAlign: "center",
                }}
              >
                {editShiftDetails?.name}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: themes?.whiteColor,
              }}
            >
              <TableContainer sx={{ mt: 2 }}>
                <Table
                  className="border"
                  sx={{
                    "& td, & th": {
                      padding: "8px 8px",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: themes?.primary,
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          fontSize: "17px",
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        colSpan={3}
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          fontSize: "17px",
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        Start Timing
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          fontSize: "17px",
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        Break
                      </TableCell>
                      <TableCell
                        colSpan={3}
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          fontSize: "17px",
                        }}
                      >
                        End Timing
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        backgroundColor: themes?.primary,
                        "& .MuiTableCell-root": {
                          textAlign: "center",
                          fontWeight: 600,
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                          fontSize: "15px",
                        },
                        "& .MuiTableCell-root:last-child": {
                          borderRight: "none",
                        },
                      }}
                    >
                      {/* Remove bottom border for Name and Break cells */}
                      <TableCell sx={{ borderBottom: "none" }}></TableCell>
                      <TableCell>Min</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>Max</TableCell>
                      <TableCell sx={{ borderBottom: "none" }}></TableCell>
                      <TableCell>Min</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell>Max</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-root": {
                          textAlign: "center",
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                          fontSize: "16px",
                        },
                        "& .MuiTableCell-root:last-child": {
                          borderRight: "none",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        First
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.min_start_time ?? "08:47"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {editShiftDetails?.start_time ?? "09:00"}
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.max_start_time ?? "09:14"}
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.break_duration ?? "00"}
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.min_end_time ?? "18:48"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {editShiftDetails?.end_time ?? "19:00"}
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.max_end_time ?? "19:12"}
                      </TableCell>
                    </TableRow>

                    <TableRow
                      sx={{
                        "& .MuiTableCell-root": {
                          textAlign: "center",
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                          fontSize: "16px",
                        },
                        "& .MuiTableCell-root:last-child": {
                          borderRight: "none",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          borderRight: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        Overtime
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        {editShiftDetails?.ot_start_time ?? "09:00"}
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.ot_max_start_time ?? "09:05"}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        {editShiftDetails?.ot_min_end_time ?? "18:45"}
                      </TableCell>
                      <TableCell>
                        {editShiftDetails?.ot_end_time ?? "19:00"}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={isEditPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleEditClosePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.whiteColor,
          },
        }}
      >
        <EditShiftPanel
          onClose={handleEditClosePanel}
          editShiftDetails={editShiftDetails}
          reCallApi={reCallApi}
          setUpdateData={setUpdateData}
          setGetId={setGetId}
        />
      </Drawer>
    </>
  );
};
